if (!customElements.get('petomg-unit-purchase')) {
  class PetomgUnitPurchase extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const {signal} = this.abort;
      this.modal = this.closest('modal-dialog');
      this.list = this.querySelector('[data-pup-units]');
      this.status = this.querySelector('[data-pup-status]');
      this.cartLines = this.querySelector('[data-pup-cart-lines]');
      this.quantities ||= new Map();
      this.indexUnits();
      this.addEventListener('change', event => this.storeQuantity(event.target), {signal});
      this.addEventListener('focusout', event => this.storeQuantity(event.target), {signal});
      this.addEventListener('submit', event => this.storeQuantity(event.target.querySelector('.pup-unit__quantity input')), {capture: true, signal});
      this.bindCartFeedback(signal);
    }

    bindCartFeedback(signal) {
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-pup-remove]');
        if (button) this.removeLine(button);
      }, {signal});
      for (const name of ['on:cart:add', 'on:line-item:change']) {
        this.addEventListener(name, event => this.onAddSuccess(event), {signal});
      }
      this.addEventListener('on:cart:error', event => this.onAddError(event), {signal});
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.feedbackTimer);
      this.feedbackIcon?.classList.remove('pup-cart-added');
    }

    open({opener, orderedIds, templates, title, note} = {}) {
      if (orderedIds && templates) {
        this.list.replaceChildren();
        for (const id of orderedIds) {
          const template = templates.get(Number(id));
          if (template) this.list.append(template.content.cloneNode(true));
        }
      }
      if (title) this.querySelector('[data-pup-title]').textContent = title;
      if (note) this.querySelector('[data-pup-note]').textContent = note;
      this.indexUnits();
      this.list.querySelectorAll('.pup-unit__quantity input').forEach(input => {
        const key = input.closest('.pup-unit')?.dataset.unitProductId;
        if (this.quantities.has(key)) input.value = this.quantities.get(key);
        this.normalizeQuantity(input);
      });
      this.setStatus('Loading cart...');
      this.cartLines.replaceChildren();
      this.modal.open(opener);
      this.loadCart();
    }

    indexUnits() {
      this.units = new Map([...this.list.querySelectorAll('[data-unit-variant-id]')]
        .map(row => [Number(row.dataset.unitVariantId), row.dataset.unitSku]));
      this.cart = null;
      this.cartLoadVersion = (this.cartLoadVersion || 0) + 1;
    }

    storeQuantity(input) {
      if (!input?.matches('.pup-unit__quantity input')) return;
      this.normalizeQuantity(input);
      const key = input.closest('.pup-unit')?.dataset.unitProductId;
      if (key) this.quantities.set(key, input.value);
    }

    normalizeQuantity(input) {
      const min = Math.max(1, Number(input.min) || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Math.max(1, Number(input.step) || 1);
      const raw = Number(input.value);
      const safe = Number.isFinite(raw) ? Math.floor(raw) : min;
      const quantity = Math.min(max, min + Math.floor((Math.max(min, safe) - min) / step) * step);
      input.value = String(quantity);
      const control = input.closest('quantity-input');
      control.querySelector('[name="minus"]').disabled = quantity <= min;
      control.querySelector('[name="plus"]').disabled = quantity >= max;
    }

    onAddSuccess(event) {
      const form = event.target.closest('product-form')?.querySelector('form');
      if (!form || !this.contains(form)) return;
      this.cartLoadVersion++;
      if (event.detail?.cart) this.renderCartLines(event.detail.cart);
      else this.loadCart(true);
      const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;
      this.setStatus(`${form.dataset.unitSku} · ${quantity} added to cart.`);
      this.shakeCartIcon();
    }

    onAddError(event) {
      const form = event.target.closest('product-form')?.querySelector('form');
      if (!form || !this.contains(form)) return;
      this.setStatus(event.detail?.error || 'Unable to add this piece. Please try again.', true);
      setTimeout(() => form.querySelector('[name="add"]')?.classList.remove('is-success'), 0);
    }

    setStatus(message, error = false) {
      this.status.textContent = message;
      this.status.toggleAttribute('data-error', error);
    }

    cartMessage(hasLines) {
      if (this.dataset.pupCartKind === 'set') return hasLines ? 'Complete set in your cart.' : 'No complete sets added.';
      return hasLines ? 'Individual pieces in your cart.' : 'No individual pieces added.';
    }

    renderCartLines(cart) {
      if (!Array.isArray(cart?.items)) return;
      this.cart = cart;
      this.cartLines.replaceChildren();
      for (const item of cart.items) {
        const sku = this.units.get(Number(item.variant_id));
        if (!sku || item.parent_relationship || item.properties?._petomg_bundle_id) continue;
        const line = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = `${sku} · Quantity: ${item.quantity}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${sku} from cart`);
        remove.dataset.pupRemove = item.key;
        line.append(label, remove);
        this.cartLines.append(line);
      }
    }

    async fetchCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {signal: this.abort.signal});
      if (!response.ok) throw new Error('Unable to load the cart.');
      return response.json();
    }

    async loadCart(preserveStatus = false) {
      const version = ++this.cartLoadVersion;
      if (!preserveStatus) this.setStatus('Loading cart...');
      try {
        const cart = await this.fetchCart();
        if (version !== this.cartLoadVersion) return;
        this.renderCartLines(cart);
        if (!preserveStatus) this.setStatus(this.cartMessage(this.cartLines.childElementCount));
      } catch {
        if (!this.abort.signal.aborted && version === this.cartLoadVersion) {
          this.setStatus('Unable to load cart items. Please try again.', true);
        }
      }
    }

    async removeLine(button) {
      if (this.removingLine) return;
      const key = button.dataset.pupRemove;
      const item = this.cart?.items?.find(line => line.key === key);
      const sku = item && this.units.get(Number(item.variant_id));
      if (!sku || item.parent_relationship || item.properties?._petomg_bundle_id) {
        this.setStatus('This cart line has changed. Reopen the list and try again.', true);
        return;
      }
      this.removingLine = true;
      button.disabled = true;
      this.cartLoadVersion++;
      this.setStatus(`Removing ${sku}...`);
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender().map(section => section.section) || ['cart-icon-bubble'];
      let mutationApplied = false;
      try {
        if (drawer) await this.waitForDrawerLine(drawer, key, item.quantity);
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
          body: JSON.stringify({id: key, quantity: 0, sections, sections_url: location.pathname})
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to remove this piece.');
        mutationApplied = true;
        this.renderCartLines(cart);
        if (drawer && sections.every(section => typeof cart.sections?.[section] === 'string')) {
          await drawer.renderContents(cart, false);
        } else if (drawer) {
          await drawer.refresh(true);
        } else if (typeof cart.sections?.['cart-icon-bubble'] === 'string') {
          customElements.get('product-form')?.updateCartIcon(cart);
        }
        this.setStatus(this.cartLines.childElementCount ? 'Cart updated.' : this.cartMessage(false));
        this.focusCartControl();
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {cart, variantId: Number(item.variant_id), oldQuantity: item.quantity, newQuantity: 0}
        }));
      } catch {
        try {
          const current = await this.fetchCart();
          this.renderCartLines(current);
          if (drawer) await drawer.refresh(true);
          if (mutationApplied) {
            this.setStatus(this.cartLines.childElementCount ? 'Cart updated.' : this.cartMessage(false));
            this.focusCartControl();
          } else {
            this.setStatus('Could not confirm removal. Cart refreshed; please review and retry.', true);
            this.focusCartControl(key);
          }
        } catch {
          this.setStatus('Cart status is uncertain. Please reopen the cart to check.', true);
        }
      } finally {
        this.removingLine = false;
        button.disabled = false;
      }
    }

    async waitForDrawerLine(drawer, key, quantity) {
      const hasLine = () => [...drawer.querySelectorAll('[data-line-key]')]
        .some(row => row.dataset.lineKey === key
          && Number(row.querySelector('.qty-input__input')?.value) === quantity);
      if (hasLine()) return;
      await new Promise(resolve => {
        const observer = new MutationObserver(() => {
          if (!hasLine()) return;
          observer.disconnect();
          clearTimeout(timeout);
          resolve();
        });
        observer.observe(drawer, {childList: true, subtree: true});
        const timeout = setTimeout(() => {
          observer.disconnect();
          resolve();
        }, 3000);
      });
      if (!hasLine()) await drawer.refresh(true);
      if (!hasLine()) throw new Error('The cart is still updating. Please try again.');
    }

    focusCartControl(key) {
      const buttons = [...this.cartLines.querySelectorAll('[data-pup-remove]')];
      const target = buttons.find(button => button.dataset.pupRemove === key)
        || buttons[0] || this.querySelector('.js-close-modal') || this.modal?.querySelector('.js-close-modal');
      target?.focus({preventScroll: true});
    }

    shakeCartIcon() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const icon = document.getElementById('cart-icon');
      if (!icon) return;
      clearTimeout(this.feedbackTimer);
      this.feedbackIcon = icon;
      icon.classList.remove('pup-cart-added');
      void icon.offsetWidth;
      icon.classList.add('pup-cart-added');
      this.feedbackTimer = setTimeout(() => icon.classList.remove('pup-cart-added'), 1000);
    }
  }

  class PetomgSetPurchase extends PetomgUnitPurchase {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.modal = this.closest('modal-dialog');
      this.status = this.querySelector('[data-pup-status]');
      this.cartLines = this.querySelector('[data-pup-cart-lines]');
      this.units = new Map();
      this.cartLoadVersion ||= 0;
      this.bindCartFeedback(this.abort.signal);
    }

    activate(variantId, sku) {
      this.units = new Map([[Number(variantId), sku]]);
      this.cart = null;
      this.cartLoadVersion++;
      this.querySelector('form[data-cwp-kind="set"]')?.setAttribute('data-unit-sku', sku);
      this.cartLines.replaceChildren();
      this.loadCart();
    }
  }

  customElements.define('petomg-unit-purchase', PetomgUnitPurchase);
  customElements.define('petomg-set-purchase', PetomgSetPurchase);
}
